import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components/macro";

import { atomicConfig } from "../../../config";
import { trackEvent } from "../../../utils/analytics";
import { call_api } from "../../../utils/services";
import { ReactComponent as Logo } from "../../../vendor/logo-dark.svg";

function extractErrorCode(err) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    return data.error || data.message || null;
  }
  return null;
}

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const Brand = styled(Logo)`
  width: 150px;
  height: 50px;
  margin-bottom: 20px;
`;

const LogoCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function AmazonCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const [status, setStatus] = useState("processing");

  useEffect(() => {
    const processCallback = async () => {
      const spapi_oauth_code = searchParams.get("spapi_oauth_code");
      const state = searchParams.get("state");
      const selling_partner_id = searchParams.get("selling_partner_id");

      if (!spapi_oauth_code) {
        navigate(
          `/auth/amazon/error?message=${encodeURIComponent(
            "Missing authorization code from Amazon."
          )}`
        );
        return;
      }

      try {
        const url = `${atomicConfig.userServiceUrl}/amazon/callback`;
        // No-auth call. The backend opportunistically reads the JWT header
        // when present (in-dashboard path) and otherwise resolves the user
        // from `state`. When neither yields a user (e.g. marketplace/email
        // entry without a session), it falls back to writing a row to
        // amazon_pending_credentials and returns {pending: true, temp_token}.
        const result = await call_api(url, "POST", {
          spapi_oauth_code,
          state,
          selling_partner_id,
        });
        const body = result?.data || {};

        // Pending path: backend stored the row in amazon_pending_credentials
        // (keyed by selling_partner_id) and will reconcile it to a user
        // out-of-band. We intentionally ignore body.temp_token - the FE no
        // longer redeems anything. From the user's perspective the
        // connection succeeded; treat it the same as the immediate path.
        if (body.pending === true) {
          trackEvent("amazon_callback_pending");
          navigate("/auth/amazon/success");
          return;
        }

        // Body-level error guard (in case the backend ever returns a 2xx
        // with {error: "..."} - it currently throws on 4xx/5xx via axios).
        if (body.error) {
          trackEvent("amazon_callback_failed", { error: body.error });
          navigate(
            `/auth/amazon/error?message=${encodeURIComponent(body.error)}`
          );
          return;
        }

        trackEvent("amazon_callback_success");
        navigate("/auth/amazon/success");
      } catch (error) {
        const code = extractErrorCode(error);
        trackEvent("amazon_callback_failed", {
          error: code || error?.message || "Unknown error",
        });
        navigate(
          `/auth/amazon/error?message=${encodeURIComponent(
            code || error?.message || "Failed to complete Amazon authorization."
          )}`
        );
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <React.Fragment>
      <Wrapper>
        <LogoCenter>
          <Brand />
        </LogoCenter>
        <Helmet title="Connecting Amazon Account..." />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress size={48} />

          <Typography component="h1" variant="h5" align="center">
            Connecting your Amazon account...
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ color: "text.secondary" }}
          >
            Please wait while we complete the authorization process.
          </Typography>
        </Box>
      </Wrapper>
    </React.Fragment>
  );
}

export default AmazonCallback;
